import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface SlideModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const SlideModal: React.FC<SlideModalProps> = ({ isOpen, onClose, title, children }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
    } else if (shouldRender) {
      setIsClosing(true);
      // Attendre la fin de l'animation avant de ne plus rendre le composant
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
        document.body.style.overflow = 'unset';
      }, 300);
      return () => clearTimeout(timer);
    }
    
    return () => {
      if (!isOpen) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, shouldRender]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!shouldRender) return null;

  // Déterminer la largeur selon le titre de la modal
  const isProspectModal = title.toLowerCase().includes('prospect');
  const isPromoterModal = title.toLowerCase().includes('promoter');
  const isTestimonialModal = title.toLowerCase().includes('testimonial');
  const isUseCaseModal = title.toLowerCase().includes('use case');
  const modalWidth = (isProspectModal || isPromoterModal) ? 'w-[800px] max-w-[90vw]' : 
                     (isTestimonialModal || isUseCaseModal) ? 'w-[1000px] max-w-[95vw]' : 'w-[500px] max-w-[90vw]';
  return (
    <>
      <div 
        className={`modal-overlay ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`} 
        onClick={handleOverlayClick} 
      />
      <div className={`slide-modal ${modalWidth} ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white modal-content">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 p-6 overflow-y-auto bg-white modal-content" style={{ maxHeight: 'calc(100vh - 80px)' }}>
          {children}
        </div>
      </div>
    </>
  );
};

export default SlideModal;